import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import MuiListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Tooltip from '@material-ui/core/Tooltip'
import ExpandLess from 'components/material-symbol/icons/ExpandLess'
import ExpandMore from 'components/material-symbol/icons/ExpandMore'
import List from '@material-ui/core/List'
import Collapse from '@material-ui/core/Collapse'
import { withStyles, makeStyles } from '@material-ui/core/styles'
import { useIsTablet } from 'lib/hooks'
import { NavLink, useLocation } from 'react-router-dom'
import { useGetCurrentPrograms } from './api-hooks'
import { PROGRAM_ROUTES } from 'lib/junior-league-utils/program-categories'
import {
  drawerWidth,
  desktopRailWidth,
  NAV_LABEL_LEFT,
} from 'components/app-shell/styles'

// Mirrors the geometry of SidebarItemShell so this header and its program
// links line up with the other nav items: same pill height, gaps, label size
// and label left edge, with a fixed width so nothing reflows while the drawer
// paper animates.
const ITEM_INSET = 12
const ITEM_HEIGHT = 44
const ITEM_GAP = 4
const reducedMotion = '@media (prefers-reduced-motion: reduce)'

const listItemStyles = theme => ({
  root: {
    color: theme.palette.sidebar.itemText,
    borderRadius: theme.palette.sidebar.itemBorderRadius,
    boxSizing: 'border-box',
    marginLeft: ITEM_INSET,
    marginRight: ITEM_INSET,
    marginBottom: ITEM_GAP,
    width: drawerWidth - ITEM_INSET * 2,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: NAV_LABEL_LEFT - ITEM_INSET,
    paddingRight: 16,
    minHeight: ITEM_HEIGHT,
    overflow: 'hidden',
    transition: theme.transitions.create(
      ['background-color', 'background-image', 'width', 'margin', 'padding'],
      {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeInOut,
      },
    ),
    [reducedMotion]: { transition: 'none' },
    '&:hover': {
      backgroundColor: theme.palette.sidebar.hoverOverlay,
    },
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.focusOverlay,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.sidebar.activeBg,
      color: theme.palette.sidebar.itemText,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.sidebar.activeBg,
    },
    '&.Mui-selected.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.activeBg,
      backgroundImage: `linear-gradient(${theme.palette.sidebar.focusOverlay}, ${theme.palette.sidebar.focusOverlay})`,
    },
    '& .MuiListItemText-root': {
      margin: 0,
      minWidth: 0,
      overflow: 'hidden',
    },
  },
})
const ListItem = withStyles(listItemStyles)(MuiListItem)

const labelTypographyStyle = {
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 500,
}

const programListItemTextProps = name => ({
  style: labelTypographyStyle,
  noWrap: true,
  title: name,
})

const ICON_CLASS = 'JrlProgramsSubMenu-icon'

const COLLAPSED_ITEM_SIZE = 48

const useSubMenuStyles = makeStyles({
  collapsedItem: ({ railWidth }) => ({
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: (railWidth - COLLAPSED_ITEM_SIZE) / 2,
    marginRight: (railWidth - COLLAPSED_ITEM_SIZE) / 2,
    width: COLLAPSED_ITEM_SIZE,
    minHeight: COLLAPSED_ITEM_SIZE,
  }),
  iconWrapper: {
    color: 'inherit',
    minWidth: 40,
    [`& .${ICON_CLASS}`]: {
      height: 20,
      width: 20,
      fontSize: 20,
    },
  },
  iconWrapperCollapsed: {
    minWidth: 0,
    justifyContent: 'center',
  },
})

export const ProgramsSubMenu = ({
  onClick,
  isTabletNavOpen,
  setIsTabletNavOpen,
  collapsed = false,
  railWidth = desktopRailWidth,
  icon,
  label = 'Current Programs',
}) => {
  const { pathname } = useLocation()
  const { data: programs, loading } = useGetCurrentPrograms()
  const filteredPrograms = programs?.filter(
    program =>
      program.year >= new Date().getFullYear() || program.year === null,
  )
  const isTablet = useIsTablet()
  const classes = useSubMenuStyles({ railWidth })
  const iconComponent = icon
    ? React.cloneElement(icon, { className: ICON_CLASS })
    : null

  const [open, setOpen] = useState(false)
  const handleExpand = () => setOpen(s => !s)

  const handleNavbarExpand = () => setIsTabletNavOpen(s => !s)

  useEffect(() => {
    if (!loading) {
      setOpen(true)
    }
  }, [loading, setOpen])

  const headerItem = (
    <ListItem
      button
      onClick={isTablet ? handleNavbarExpand : handleExpand}
      disabled={loading || filteredPrograms?.length === 0}
      className={clsx({ [classes.collapsedItem]: collapsed })}
    >
      {collapsed && iconComponent ? (
        <ListItemIcon
          className={clsx(classes.iconWrapper, classes.iconWrapperCollapsed)}
        >
          {iconComponent}
        </ListItemIcon>
      ) : (
        <>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              style: labelTypographyStyle,
              noWrap: true,
            }}
          />
          {open ? (
            <ExpandLess
              style={{ display: !isTabletNavOpen ? 'none' : 'block' }}
            />
          ) : (
            <ExpandMore />
          )}
        </>
      )}
    </ListItem>
  )

  return (
    <>
      {collapsed ? (
        <Tooltip title={label} placement="right" enterDelay={200}>
          {headerItem}
        </Tooltip>
      ) : (
        headerItem
      )}
      {isTabletNavOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {(filteredPrograms || []).map(program => (
              <ListItem
                key={program.id}
                button
                onClick={onClick}
                selected={pathname.includes(program.id)}
                component={NavLink}
                to={PROGRAM_ROUTES[program.category].program(
                  program.programLocationId,
                  program.id,
                )}
              >
                <ListItemText
                  primary={program.name}
                  primaryTypographyProps={programListItemTextProps(
                    program.name,
                  )}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}
